import { Add } from "@mui/icons-material";
import { Button } from "@mui/material";
import { useState } from "react";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import ConnectionCard from "./ConnectionCard";
import ConnectionFormDialog from "./ConnectionFormDialog";
import { useMarketingActions } from "./useMarketing";

export default function ConnectionPanel({ connections, canConnect }) {
  const { createConnection, updateConnection, removeConnection, testConnection } =
    useMarketingActions();
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [results, setResults] = useState({});

  const test = (id) => {
    setTestingId(id);
    testConnection.mutate(id, {
      onSuccess: (res) => setResults((current) => ({ ...current, [id]: res?.data?.data })),
      onSettled: () => setTestingId(null),
    });
  };

  const save = (form) => {
    const done = (res) => {
      setEditing(null);
      test(res?.data?.data?.id ?? editing.connection?.id);
    };
    if (editing.connection) {
      updateConnection.mutate({ id: editing.connection.id, body: form }, { onSuccess: done });
    } else {
      createConnection.mutate(form, { onSuccess: done });
    }
  };

  const confirmRemove = () =>
    removeConnection.mutate(removing.id, { onSettled: () => setRemoving(null) });

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-semibold">Meta accounts</h2>
          <p className="text-sm text-app-text-secondary">
            Connect each Facebook page and ad account you run ads for. Campaigns, posts and boosts
            are managed per account.
          </p>
        </div>
        {canConnect ? (
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => setEditing({ connection: null })}
          >
            Add account
          </Button>
        ) : null}
      </div>

      {connections.length === 0 ? (
        <div className="rounded border border-dashed border-app-border p-6 text-center text-sm text-app-text-secondary">
          <p className="font-medium text-app-text">No Meta account connected.</p>
          <p>
            {canConnect
              ? "Add one to browse posts and manage ads."
              : "Ask someone with Meta account access to connect one."}
          </p>
        </div>
      ) : null}

      {connections.map((connection) => (
        <ConnectionCard
          key={connection.id}
          connection={connection}
          canConnect={canConnect}
          testing={testingId === connection.id}
          result={results[connection.id]}
          onTest={(item) => test(item.id)}
          onEdit={(item) => setEditing({ connection: item })}
          onRemove={setRemoving}
        />
      ))}

      {editing ? (
        <ConnectionFormDialog
          connection={editing.connection}
          saving={createConnection.isLoading || updateConnection.isLoading}
          onSave={save}
          onClose={() => setEditing(null)}
        />
      ) : null}
      <ConfirmDialog
        open={Boolean(removing)}
        title="Remove Meta account"
        message={
          removing
            ? `Remove "${removing.label}" from the dashboard? Its campaigns and saved results disappear here, and the saved token is deleted. Nothing is changed on Meta. Accounts with running campaigns cannot be removed until those are paused.`
            : ""
        }
        confirmLabel="Remove"
        confirmColor="warning"
        loading={removeConnection.isLoading}
        onConfirm={confirmRemove}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
